<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <Teleport to="body">
    <!-- autofocus: the dialog itself takes the focus as it opens, not ✕ (no ring on a tap) -->
    <dialog ref="dialogRef" autofocus aria-label="Menu" @click="closeDialog">
      <nav>
        <menu>
          <!-- left-aligned items and ✕ at the top right, as Apple's menu (2026-10-14) -->
          <button id="close" type="button" aria-label="Close menu">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>
          <PrimaryItem
            v-for="view in views"
            :key="view"
            class="primaryView"
            :to="getViewPath(view)"
            :selected="view === selected">
            {{ view }}
          </PrimaryItem>
          <Archive id="archive" />
          <!-- the mailing lists, back since 2026-10-23 (DL16): "Welcome to VIZBI" signed out,
               "Subscribe" or "Subscriptions" signed in (ButtonMail.vue); never an empty bar -->
          <ButtonMail id="mailButton" />
          <div id="menuEnd" />
        </menu>
      </nav>
    </dialog>
  </Teleport>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line
const props = defineProps(["views", "selected"]); // eslint-disable-line
import PrimaryItem from "@/components/Navigation/Primary/Item.vue";
import Archive from "@/components/Navigation/Archive.vue";
import ButtonMail from "@/components/ButtonMail.vue";
import {getViewPath} from "@/components/Navigation/Primary/viewPaths";
// function to open dialog
import {ref} from "vue";
const dialogRef = ref(null); // ref to dialog HTML element
const body = document.body;
function openDialog() {
  log(`Open dialog`);
  if (!dialogRef.value) {
    console.error(`dialogRef.value is falsy`);
    return;
  }
  body.classList.add("hide-stage"); // show-hide-stage.css
  dialogRef.value.classList.add("show-curtain"); // show-hide-curtain.css
  setTimeout(() => {
    body.style.overflowX = "hidden";
    body.style.overflowY = "hidden";
    body.style.height = "100%"; //100dvh 100%
    dialogRef.value.showModal();
  }, 200);
  // history.pushState({}, null, `${location.pathname}#menu`);
  location.hash = "#menu";
}
// the page as it was before the menu opened: shown (no stage clip), scrolling again
function restoreBody() {
  body.classList.remove("hide-stage", "show-stage");
  body.style.overflowX = "auto";
  body.style.overflowY = "auto";
  body.style.height = "auto";
}
// function to close dialog
const emit = defineEmits(["close"]);
// closed once: a tap on a link closes it (the dialog's click) and the route change that follows
// would close it again, after it has gone (dialogRef null: an uncaught error, 2026-10-14)
function closeDialog(target) {
  log(`Close dialog`);
  if (!dialogRef.value?.open) return;
  if (target) log(`click target: ${JSON.stringify(target)}`);
  dialogRef.value.close();
  body.classList.remove("hide-stage");
  body.classList.add("show-stage"); // show-hide-stage.css
  dialogRef.value.classList.add("hide-curtain"); // show-hide-curtain.css
  setTimeout(restoreBody, 1000);
  emit("close");
  // history.pushState({}, null, `${location.pathname}`);
  // only its own #menu: a link's hash (#sign-in, #subscriptions) stays (2026-10-23)
  if (location.hash === "#menu") location.hash = "";
}
// close dialog when route changes
import {watch} from "vue";
import {useRoute} from "vue-router";
const route = useRoute();
watch(
  route,
  () => {
    log(`route changed`);
    if (location.hash === "#menu") return;
    if (dialogRef.value?.open) closeDialog();
  },
  {immediate: false} // don't run on first load
);
// open dialog on mount
import {onBeforeUnmount, onMounted} from "vue";
onMounted(() => {
  openDialog();
});
// unmounted while open: a link to a page without the header (/About) removes the menu with it
// before it closes, which left the page clipped away, a black screen (2026-11-05)
onBeforeUnmount(() => {
  if (body.classList.contains("hide-stage")) restoreBody();
});
</script>

<style scoped>
/* There is no need for hover here because: */
/* (1) narrow screen (generally) don't have mouse */
/* (2) Anyway, I want to remove most hover states, following Apple design */
/* (3) Hover states greatly increase complexity, fragility, and maintenance */
/* (4) Hover states have few benefits beyond what the cursor:pointer provides */

/* * {
  background-color: rgb(100 0 0 / 0.1) !important;
} */
dialog {
  width: 100%; /* CRITICAL */
  max-width: 100%; /* CRITICAL: https://stackoverflow.com/q/75024007  */
  padding: 0.5rem 2rem;
  margin: 0;
  border: none;
  box-sizing: border-box; /* CRITICAL: https://stackoverflow.com/q/42541657 */
  height: 100dvh; /* 100dvh 100% */
  max-height: 100dvh; /* without this, black bar */
  min-height: 100dvh;
  font-size: 1.7rem;
  font-weight: 700;
  /* a plain background, the grey the open menu already had below the header: none of the page's
     banner image shows through (it's the body's background, painted on the whole page) */
  background-color: var(--background-color-subtle);
}
nav {
  padding: 0;
  height: 100%;
  box-sizing: border-box; /* CRITICAL: https://stackoverflow.com/q/42541657 */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
menu {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0; /* needed to undo padding on dialog */
  margin: 0; /* removes black bar at bottom */
  box-sizing: border-box; /* CRITICAL */
  /* overflow-x: hidden; */
  overflow-y: hidden;
  height: 100dvh; /* 100dvh 100% */
  /* max-height: 100dvh; without this, black bar */
  /* min-height: 100dvh; without this, black bar */
}
/* ✕: a thin line icon in a tap-sized button, inside the margins so its focus ring is never
   clipped; the global button look reset */
#close {
  flex: 0 0 auto;
  align-self: flex-end;
  width: 2.75rem;
  height: 2.75rem;
  margin: 0.75rem -0.6rem 0.5rem 0;
  padding: 0.55rem;
  border: none;
  border-radius: 50%;
  box-shadow: none;
  color: var(--primary-navigation-color);
  background-color: transparent;
  cursor: pointer;
}
#close svg {
  display: block;
  width: 100%;
  height: 100%;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
}
#close:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: -2px;
}
/* the items left-aligned (Item.vue right-aligns them in the wide navigation) */
.primaryView {
  text-align: left;
}
.lightMode #close {
  color: var(--text-color);
  /* text-shadow: none; */
  /* text-shadow: rgb(255, 255, 255, 0.7) 0 0 0.375rem; */
}
menu :deep(a) {
  /* selects only main items, not archive */
  display: inline-flex;
  color: var(--primary-navigation-color); /* override default link color */
  text-shadow: rgb(0, 0, 0) 0 0 0.375rem;
}
.lightMode menu :deep(a) {
  color: var(--text-color-less-subtle);
  /* text-shadow: none; */
  text-shadow: rgb(255, 255, 255, 0.7) 0 0 0.375rem;
}
.primaryView {
  flex: 1;
}
#archive {
  flex: 5;
  /* height: 250px; */
}
/* below the archive, as before 2026-10-14 */
#mailButton {
  flex: 0; /* its own height */
  margin-top: 1.5rem;
  cursor: pointer;
}
#mailButton :deep(a) {
  display: flex; /* not right-aligned */
}
/* the space below the archive */
#menuEnd {
  flex: 5;
}
@media (max-height: 700px) {
  /* short phones */
  menu > * {
    flex: 0.45; /* first seven main items */
  }

  dialog {
    font-size: 1.5rem;
  }
  menu :deep(a) {
    line-height: 2.2rem;
  }
  #menuEnd {
    flex: 1.8;
  }
}
@import "@/styles/show-hide-curtain.css";
</style>
